import { Injectable } from '@angular/core';
import { EConfigurationChannel, IConfiguration } from '@web/dtos';
import {
  ConfigurationControlValue,
  IConfigurationFormData,
} from '../../../entities/configuration-entity';

@Injectable({ providedIn: 'root' })
export class ConfigurationChannelService {
  public settingsForKey(
    configuration: IConfiguration[],
    settingKey: string
  ): IConfiguration[] {
    return configuration.filter(
      (setting: IConfiguration): boolean => setting.settingKey === settingKey
    );
  }

  public settingForChannel(
    settings: IConfiguration[],
    channel: EConfigurationChannel
  ): IConfiguration | undefined {
    return settings.find((setting: IConfiguration): boolean =>
      setting.channels.includes(channel)
    );
  }

  public haveSameValue(
    firstValue: ConfigurationControlValue,
    secondValue: ConfigurationControlValue
  ): boolean {
    return JSON.stringify(firstValue) === JSON.stringify(secondValue);
  }

  public haveMixedDisabledState(settings: IConfiguration[]): boolean {
    const disabledStates: boolean[] = settings.map(
      (setting: IConfiguration): boolean => setting.isDisabled === true
    );
    return disabledStates.includes(true) && disabledStates.includes(false);
  }

  public areAllSettingsDisabled(settings: IConfiguration[]): boolean {
    return (
      settings.length > 0 &&
      settings.every(
        (setting: IConfiguration): boolean => setting.isDisabled === true
      )
    );
  }

  public mergeEqualChannelSettings(
    configuration: IConfiguration[],
    valueForSetting: (
      setting: IConfiguration
    ) => ConfigurationControlValue
  ): IConfiguration[] {
    let normalizedConfiguration: IConfiguration[] = configuration;
    const settingKeys: Set<string> = new Set(
      configuration.map(
        (setting: IConfiguration): string => setting.settingKey
      )
    );

    settingKeys.forEach((settingKey: string): void => {
      const settings: IConfiguration[] = this.settingsForKey(
        normalizedConfiguration,
        settingKey
      );
      if (settings.length !== 2) {
        return;
      }

      const webSetting: IConfiguration | undefined = this.settingForChannel(
        settings,
        EConfigurationChannel.Web
      );
      const appSetting: IConfiguration | undefined = this.settingForChannel(
        settings,
        EConfigurationChannel.App
      );

      if (
        !webSetting ||
        !appSetting ||
        webSetting === appSetting ||
        webSetting.channels.length !== 1 ||
        appSetting.channels.length !== 1 ||
        this.haveMixedDisabledState(settings) ||
        (!this.areAllSettingsDisabled(settings) &&
          !this.haveSameValue(
            valueForSetting(webSetting),
            valueForSetting(appSetting)
          ))
      ) {
        return;
      }

      normalizedConfiguration = this.replace(
        normalizedConfiguration,
        settingKey,
        this.merge(settings, EConfigurationChannel.Web)
      );
    });

    return normalizedConfiguration;
  }

  public split(setting: IConfiguration): IConfiguration[] {
    if (setting.isDisabled === true) {
      return [setting];
    }

    const webSetting: IConfiguration = { ...setting };
    const appSetting: IConfiguration = { ...setting };
    delete webSetting.id;
    delete appSetting.id;

    webSetting.channels = [EConfigurationChannel.Web];
    appSetting.channels = [EConfigurationChannel.App];
    return [webSetting, appSetting];
  }

  public merge(
    settings: IConfiguration[],
    selectedChannel: EConfigurationChannel
  ): IConfiguration[] {
    if (this.haveMixedDisabledState(settings)) {
      return settings;
    }

    const selectedSetting: IConfiguration | undefined =
      this.settingForChannel(settings, selectedChannel) ?? settings[0];

    return selectedSetting
      ? [
          {
            ...selectedSetting,
            channels: [EConfigurationChannel.Web, EConfigurationChannel.App],
          },
        ]
      : [];
  }

  public replace(
    configuration: IConfiguration[],
    settingKey: string,
    updatedSettings: IConfiguration[]
  ): IConfiguration[] {
    const replacementIndex: number = configuration.findIndex(
      (setting: IConfiguration): boolean => setting.settingKey === settingKey
    );

    return configuration.flatMap(
      (setting: IConfiguration, index: number): IConfiguration[] => {
        if (setting.settingKey !== settingKey) {
          return [setting];
        }

        return index === replacementIndex ? updatedSettings : [];
      }
    );
  }

  public haveSameFormState(
    initialConfiguration: IConfigurationFormData[],
    currentConfiguration: IConfigurationFormData[]
  ): boolean {
    return (
      JSON.stringify(this.withoutIds(initialConfiguration)) ===
      JSON.stringify(this.withoutIds(currentConfiguration))
    );
  }

  private withoutIds(
    configuration: IConfigurationFormData[]
  ): Array<Omit<IConfigurationFormData, 'id'>> {
    return configuration.map(
      (setting: IConfigurationFormData): Omit<IConfigurationFormData, 'id'> => {
        const copy: Partial<IConfigurationFormData> = { ...setting };
        delete copy.id;
        return copy as Omit<IConfigurationFormData, 'id'>;
      }
    );
  }
}
